/**
 * Global axios response interceptor: on 401 with a valid refresh_token in
 * localStorage, silently refresh the access token and retry the original request.
 * Only kicks out to /admin/login when refresh itself fails (true dead session).
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let refreshing = null; // shared promise so concurrent 401s trigger only one refresh

function kickToLogin() {
  localStorage.removeItem("koko_token");
  localStorage.removeItem("koko_user");
  localStorage.removeItem("koko_refresh_token");
  // Route to the correct portal login based on current path
  const path = window.location.pathname;
  const target = path.startsWith("/staff") ? "/staff/login"
               : path.startsWith("/dashboard") || path.startsWith("/login") || path.startsWith("/signup") ? "/login"
               : "/admin/login";
  if (!path.startsWith(target)) {
    window.location.href = target;
  }
}

let installed = false;

export function installAuthInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (res) => res,
    async (err) => {
      const status = err.response?.status;
      const original = err.config;

      // Only retry 401s, only once, and only for our own API calls
      if (status !== 401 || !original || original._retried) {
        return Promise.reject(err);
      }
      // Skip retry for auth endpoints themselves (avoid infinite loop)
      const url = original.url || "";
      if (url.includes("/auth/login") || url.includes("/auth/refresh")) {
        return Promise.reject(err);
      }

      const refreshToken = localStorage.getItem("koko_refresh_token");
      if (!refreshToken) {
        kickToLogin();
        return Promise.reject(err);
      }

      try {
        if (!refreshing) {
          refreshing = axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken })
            .then((r) => {
              const newTok = r.data?.access_token;
              if (newTok) localStorage.setItem("koko_token", newTok);
              return newTok;
            })
            .finally(() => {
              // Reset after a tick so parallel retries latch onto the same refresh
              setTimeout(() => { refreshing = null; }, 0);
            });
        }
        const newToken = await refreshing;
        if (!newToken) {
          kickToLogin();
          return Promise.reject(err);
        }
        // Retry the original request with fresh token
        original._retried = true;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axios(original);
      } catch (refreshErr) {
        kickToLogin();
        return Promise.reject(refreshErr);
      }
    },
  );
}
